import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Nav from "components/layout/Nav";
import { MenuBar } from "components/layout/MenuBar";
import API from "api";
import { showToast } from "components/shared/Toast";

const DEFAULT_SECTION_TITLES = ["Pages", "About Us", "Resources", "Get In Touch"];

const createLocalId = (prefix) =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const createEmptyItem = () => ({
  id: createLocalId("item"),
  label: "",
  url: "",
  openInNewTab: false,
});

const createEmptySection = (title = "New Section") => ({
  id: createLocalId("section"),
  title,
  items: [],
});

const normalizeItem = (item = {}) => ({
  id: item.id || createLocalId("item"),
  label: item.label || "",
  url: item.url || "",
  openInNewTab: Boolean(item.openInNewTab),
});

const normalizeSection = (section = {}, index = 0) => ({
  id: section.id || createLocalId("section"),
  title: section.title || DEFAULT_SECTION_TITLES[index] || `Section ${index + 1}`,
  items: Array.isArray(section.items) ? section.items.map(normalizeItem) : [],
});

const buildInitialSections = () => DEFAULT_SECTION_TITLES.map((title) => createEmptySection(title));

const AppearanceCMS = () => {
  const { user } = useSelector((state) => state.auth);
  const isSuperAdmin = user?.role === "superAdmin" || user?.isSuperAdmin;

  const [footerSections, setFooterSections] = useState(buildInitialSections);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let ignore = false;

    const loadAppearance = async () => {
      setIsLoading(true);

      try {
        const response = await API.get("/appearance/footer");
        if (ignore) {
          return;
        }

        const sections = Array.isArray(response.data?.footerSections)
          ? response.data.footerSections.map(normalizeSection)
          : buildInitialSections();

        setFooterSections(sections.length ? sections : buildInitialSections());
      } catch (error) {
        if (!ignore) {
          showToast("Failed to load footer menu settings.", "error");
        }
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    };

    loadAppearance();

    return () => {
      ignore = true;
    };
  }, []);

  const canSave = useMemo(
    () => footerSections.some((section) => section.title.trim()),
    [footerSections]
  );

  const updateSection = (sectionId, updater) => {
    setFooterSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? typeof updater === "function"
            ? updater(section)
            : { ...section, ...updater }
          : section
      )
    );
  };

  const addSection = () => {
    setFooterSections((prev) => [...prev, createEmptySection()]);
  };

  const removeSection = (sectionId) => {
    setFooterSections((prev) => prev.filter((section) => section.id !== sectionId));
  };

  const moveSection = (sectionId, direction) => {
    setFooterSections((prev) => {
      const currentIndex = prev.findIndex((section) => section.id === sectionId);
      const nextIndex = currentIndex + direction;

      if (currentIndex < 0 || nextIndex < 0 || nextIndex >= prev.length) {
        return prev;
      }

      const nextSections = [...prev];
      const [section] = nextSections.splice(currentIndex, 1);
      nextSections.splice(nextIndex, 0, section);
      return nextSections;
    });
  };

  const addItem = (sectionId) => {
    updateSection(sectionId, (section) => ({
      ...section,
      items: [...section.items, createEmptyItem()],
    }));
  };

  const updateItem = (sectionId, itemId, field, value) => {
    updateSection(sectionId, (section) => ({
      ...section,
      items: section.items.map((item) =>
        item.id === itemId ? { ...item, [field]: value } : item
      ),
    }));
  };

  const removeItem = (sectionId, itemId) => {
    updateSection(sectionId, (section) => ({
      ...section,
      items: section.items.filter((item) => item.id !== itemId),
    }));
  };

  const handleSave = async () => {
    const trimmedSections = footerSections
      .map((section) => ({
        ...section,
        title: section.title.trim(),
        items: section.items.map((item) => ({
          ...item,
          label: item.label.trim(),
          url: item.url.trim(),
        })),
      }))
      .filter((section) => section.title);

    if (!trimmedSections.length) {
      showToast("Add at least one footer section before saving.", "warning");
      return;
    }

    const hasInvalidItem = trimmedSections.some((section) =>
      section.items.some((item) => !item.label || !item.url)
    );

    if (hasInvalidItem) {
      showToast("Each footer link needs both a label and a URL.", "warning");
      return;
    }

    setIsSaving(true);

    try {
      const response = await API.put("/appearance/footer", {
        footerSections: trimmedSections,
      });

      const sections = Array.isArray(response.data?.footerSections)
        ? response.data.footerSections.map(normalizeSection)
        : buildInitialSections();

      setFooterSections(sections.length ? sections : buildInitialSections());
      showToast("Footer menu updated successfully.", "success");
    } catch (error) {
      showToast(error?.response?.data?.message || "Failed to save footer menu.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Nav />
      <div className="row">
        <MenuBar />
        <main style={{ width: "calc(100vw - 240px)", backgroundColor: "#f5f7fb", minHeight: "100vh" }}>
          <div style={{ padding: "32px", display: "flex", flexDirection: "column", gap: "20px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: "16px",
                flexWrap: "wrap",
              }}
            >
              <div>
                <p style={{ margin: 0, color: "#64748b", fontWeight: 600, letterSpacing: "0.5px" }}>
                  Website Appearance
                </p>
                <h2 style={{ margin: "6px 0 8px", fontWeight: 700, color: "#0f172a" }}>
                  Footer Menu Manager
                </h2>
                <p style={{ margin: 0, maxWidth: "760px", color: "#475569" }}>
                  Manage footer section titles, links, and URLs here. The website footer reads this data directly, so changes show up without code edits.
                </p>
              </div>

              {isSuperAdmin && (
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={addSection}
                    style={secondaryButtonStyle}
                  >
                    Add Section
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={!canSave || isSaving}
                    style={{
                      ...primaryButtonStyle,
                      opacity: !canSave || isSaving ? 0.65 : 1,
                      cursor: !canSave || isSaving ? "not-allowed" : "pointer",
                    }}
                  >
                    {isSaving ? "Saving..." : "Save Footer Menu"}
                  </button>
                </div>
              )}
            </div>

            {!isSuperAdmin ? (
              <div style={cardStyle}>
                <h4 style={{ marginTop: 0, color: "#ef4444" }}>Access restricted</h4>
                <p style={{ marginBottom: 0, color: "#475569" }}>
                  Only the Super Admin can manage the website appearance and footer menu.
                </p>
              </div>
            ) : isLoading ? (
              <div style={cardStyle}>
                <p style={{ margin: 0 }}>Loading footer menu settings...</p>
              </div>
            ) : (
              <div style={{ display: "grid", gap: "18px" }}>
                {footerSections.map((section, sectionIndex) => (
                  <section key={section.id} style={cardStyle}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "12px",
                        flexWrap: "wrap",
                        marginBottom: "18px",
                      }}
                    >
                      <div style={{ flex: "1 1 320px" }}>
                        <label style={labelStyle}>Section Title</label>
                        <input
                          type="text"
                          value={section.title}
                          onChange={(event) =>
                            updateSection(section.id, { title: event.target.value })
                          }
                          placeholder="Enter section title"
                          style={inputStyle}
                        />
                      </div>

                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        <button
                          type="button"
                          onClick={() => moveSection(section.id, -1)}
                          disabled={sectionIndex === 0}
                          style={smallGhostButtonStyle}
                        >
                          Move Up
                        </button>
                        <button
                          type="button"
                          onClick={() => moveSection(section.id, 1)}
                          disabled={sectionIndex === footerSections.length - 1}
                          style={smallGhostButtonStyle}
                        >
                          Move Down
                        </button>
                        <button
                          type="button"
                          onClick={() => addItem(section.id)}
                          style={smallSecondaryButtonStyle}
                        >
                          Add Link
                        </button>
                        <button
                          type="button"
                          onClick={() => removeSection(section.id)}
                          disabled={footerSections.length === 1}
                          style={smallDangerButtonStyle}
                        >
                          Remove Section
                        </button>
                      </div>
                    </div>

                    {section.items.length === 0 ? (
                      <div style={emptyStateStyle}>
                        <p style={{ margin: 0, color: "#64748b" }}>
                          No links added to this section yet.
                        </p>
                      </div>
                    ) : (
                      <div style={{ display: "grid", gap: "12px" }}>
                        {section.items.map((item, itemIndex) => (
                          <div
                            key={item.id}
                            style={{
                              border: "1px solid #e2e8f0",
                              borderRadius: "14px",
                              padding: "16px",
                              background: "#f8fafc",
                              display: "grid",
                              gap: "12px",
                            }}
                          >
                            <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
                              <strong style={{ color: "#0f172a" }}>Link {itemIndex + 1}</strong>
                              <button
                                type="button"
                                onClick={() => removeItem(section.id, item.id)}
                                style={smallDangerButtonStyle}
                              >
                                Remove Link
                              </button>
                            </div>

                            <div style={linkGridStyle}>
                              <div>
                                <label style={labelStyle}>Label</label>
                                <input
                                  type="text"
                                  value={item.label}
                                  onChange={(event) =>
                                    updateItem(section.id, item.id, "label", event.target.value)
                                  }
                                  placeholder="Link text"
                                  style={inputStyle}
                                />
                              </div>

                              <div>
                                <label style={labelStyle}>URL</label>
                                <input
                                  type="text"
                                  value={item.url}
                                  onChange={(event) =>
                                    updateItem(section.id, item.id, "url", event.target.value)
                                  }
                                  placeholder="/trek or https://example.com"
                                  style={inputStyle}
                                />
                              </div>
                            </div>

                            <label style={checkboxLabelStyle}>
                              <input
                                type="checkbox"
                                checked={item.openInNewTab}
                                onChange={(event) =>
                                  updateItem(section.id, item.id, "openInNewTab", event.target.checked)
                                }
                              />
                              Open this link in a new tab
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

const cardStyle = {
  background: "#ffffff",
  borderRadius: "18px",
  padding: "22px",
  boxShadow: "0 16px 40px rgba(15, 23, 42, 0.08)",
};

const labelStyle = {
  display: "block",
  marginBottom: "6px",
  color: "#334155",
  fontWeight: 600,
  fontSize: "14px",
};

const inputStyle = {
  width: "100%",
  height: "44px",
  borderRadius: "10px",
  border: "1px solid #cbd5e1",
  padding: "0 12px",
  fontSize: "14px",
  color: "#0f172a",
  background: "#ffffff",
};

const primaryButtonStyle = {
  border: "none",
  borderRadius: "10px",
  padding: "12px 16px",
  background: "#2563eb",
  color: "#ffffff",
  fontWeight: 700,
  boxShadow: "0 12px 30px rgba(37, 99, 235, 0.28)",
};

const secondaryButtonStyle = {
  border: "1px solid #cbd5e1",
  borderRadius: "10px",
  padding: "12px 16px",
  background: "#ffffff",
  color: "#0f172a",
  fontWeight: 700,
};

const smallSecondaryButtonStyle = {
  ...secondaryButtonStyle,
  padding: "10px 12px",
  fontWeight: 600,
};

const smallGhostButtonStyle = {
  ...smallSecondaryButtonStyle,
  background: "#f8fafc",
};

const smallDangerButtonStyle = {
  border: "1px solid #fecaca",
  borderRadius: "10px",
  padding: "10px 12px",
  background: "#fff1f2",
  color: "#b91c1c",
  fontWeight: 600,
};

const emptyStateStyle = {
  border: "1px dashed #cbd5e1",
  borderRadius: "14px",
  padding: "18px",
  background: "#f8fafc",
};

const linkGridStyle = {
  display: "grid",
  gap: "12px",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
};

const checkboxLabelStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  color: "#334155",
  fontSize: "14px",
  fontWeight: 500,
};

export default AppearanceCMS;
